import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { BrowserNotificationPort } from './browser-notification.port';

/** Transient — deliberately NOT in PRESERVED_STORAGE_KEYS, so logout clears it. */
export const DESKTOP_FOCUS_CONTEXT_KEY = 'wolsal.ui.notifications.focus';

/** Older than this and the record is ignored: a dead tab must not suppress forever. */
export const DESKTOP_FOCUS_CONTEXT_STALENESS_MS = 30_000;

/**
 * How often the focused tab re-stamps its record. Comfortably under the staleness
 * window, so a user who simply reads the orders list — generating no focus, visibility
 * or navigation event — never ages out of suppression.
 */
export const DESKTOP_FOCUS_CONTEXT_HEARTBEAT_MS = 10_000;

export interface IFocusContextRecord {
  path: string;
  ts: number;
}

/** The route path without query string or fragment. Router.url carries both. */
export function pathOf(url: string): string {
  return url.split(/[?#]/)[0];
}

/**
 * The focused tab publishes what it is looking at; every tab reads it back
 * synchronously. Suppression is a decision about the user, not about this tab, so it
 * cannot be taken from this tab's own visibility or route.
 */
@Injectable({ providedIn: 'root' })
export class DesktopSuppressionContext {
  private readonly stopped$ = new Subject<void>();
  private readonly publishHandler = (): void => this.publish();
  private readonly clearHandler = (): void => this.clear();
  private heartbeat: ReturnType<typeof setInterval> | null = null;

  constructor(private router: Router, private port: BrowserNotificationPort) {}

  // ── Publishing ─────────────────────────────────────────────────────────────

  startPublishing(): void {
    document.addEventListener('visibilitychange', this.publishHandler);
    window.addEventListener('focus', this.publishHandler);
    window.addEventListener('blur', this.publishHandler);
    window.addEventListener('pagehide', this.clearHandler);

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.stopped$),
      )
      .subscribe(this.publishHandler);

    this.publish();
    this.heartbeat = setInterval(() => this.beat(), DESKTOP_FOCUS_CONTEXT_HEARTBEAT_MS);
  }

  stopPublishing(): void {
    document.removeEventListener('visibilitychange', this.publishHandler);
    window.removeEventListener('focus', this.publishHandler);
    window.removeEventListener('blur', this.publishHandler);
    window.removeEventListener('pagehide', this.clearHandler);

    if (this.heartbeat !== null) {
      clearInterval(this.heartbeat);
      this.heartbeat = null;
    }

    this.stopped$.next();
  }

  // ── Reading ────────────────────────────────────────────────────────────────

  /** The path the focused tab is on, or null when no fresh record exists. */
  focusedPath(now: number = Date.now()): string | null {
    try {
      const raw = localStorage.getItem(DESKTOP_FOCUS_CONTEXT_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (!parsed || typeof parsed.path !== 'string' || typeof parsed.ts !== 'number') {
        return null;
      }
      if (now - parsed.ts >= DESKTOP_FOCUS_CONTEXT_STALENESS_MS) {
        return null;
      }
      return parsed.path;
    } catch {
      // a malformed or unreadable record must never suppress
      return null;
    }
  }

  // ── Storage ────────────────────────────────────────────────────────────────

  /**
   * Re-stamps the record, but only ever writes. Unlike `publish`, an unfocused tab does
   * nothing here rather than clearing: several tabs beat on their own timers, and a
   * background one clearing would delete the focused tab's record.
   */
  private beat(): void {
    if (!this.port.visible || !this.port.focused) {
      return;
    }
    this.publish();
  }

  private publish(): void {
    if (!this.port.visible || !this.port.focused) {
      this.clear();
      return;
    }

    const record: IFocusContextRecord = { path: pathOf(this.router.url), ts: Date.now() };
    try {
      localStorage.setItem(DESKTOP_FOCUS_CONTEXT_KEY, JSON.stringify(record));
    } catch {
      // ignore quota / disabled storage errors — the record is best-effort
    }
  }

  private clear(): void {
    try {
      localStorage.removeItem(DESKTOP_FOCUS_CONTEXT_KEY);
    } catch {
      // ignore disabled storage errors
    }
  }
}
